import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VideoPlayer from "@/components/VideoPlayer";
import TechniqueForm from "@/components/TechniqueForm";
import AddToLibraryDialog from "@/components/AddToLibraryDialog";
import { ScopeBadge } from "@/components/ScopeBadge";
import { RichTextView } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { CATEGORY_COLORS, BELT_COLORS } from "@/lib/constants";
import { ArrowLeft, Pencil, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

export default function TechniqueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/techniques/${id}`); setT(data); }
    catch (e) { toast.error(apiError(e)); navigate("/library"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-8"><div className="h-96 rounded-xl bg-card border border-border animate-pulse" /></div>;
  if (!t) return null;

  const isPA = user?.role === "platform_admin";
  const canEdit = t.scope === "platform" ? isPA
    : isPA ? false
    : t.scope === "personal" ? t.owner_id === user?.user_id
    : (user?.role === "admin" || user?.role === "coach") && t.gym_id === user?.gym_id;
  const canAddToLibrary = t.scope === "platform" && !isPA;

  return (
    <div className="p-4 sm:p-6 md:p-8 pb-24 md:pb-8 max-w-[1600px] mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <Button data-testid="back-btn" variant="ghost" onClick={() => navigate(-1)} className="text-muted-foreground hover:text-white -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        {canAddToLibrary ? (
          <Button data-testid="detail-add-to-library-btn" onClick={() => setAddOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
            <BookmarkPlus className="h-4 w-4 mr-1.5" /> Add to Library
          </Button>
        ) : canEdit && <Button data-testid="detail-edit-btn" onClick={() => setFormOpen(true)} variant="outline" className="border-border"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_420px] gap-8">
        <div className="min-w-0">
          <VideoPlayer technique={t} />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {t.category && <span className="text-xs font-semibold px-2.5 py-1 rounded" style={{ background: `${CATEGORY_COLORS[t.category]}22`, color: CATEGORY_COLORS[t.category] }}>{t.category}</span>}
            {t.position && <span className="text-xs px-2.5 py-1 rounded bg-secondary text-secondary-foreground">{t.position}</span>}
            {t.belt_level && <span className="text-xs px-2.5 py-1 rounded border border-border flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: BELT_COLORS[t.belt_level] }} />{t.belt_level} belt</span>}
            <ScopeBadge scope={t.scope} className="border border-border text-muted-foreground text-xs px-2.5 py-1" />
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight break-words">{t.name}</h1>
          {t.owner_name && <p className="text-sm text-muted-foreground mt-2">Added by {t.owner_name}</p>}

          {t.description && (
            <div className="mt-6">
              <h2 className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">Details</h2>
              <RichTextView html={t.description} />
            </div>
          )}

          {t.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-1.5">
              {t.tags.map((tag) => <span key={tag} className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground">#{tag}</span>)}
            </div>
          )}
        </div>
      </div>

      <TechniqueForm open={formOpen} onOpenChange={setFormOpen} technique={t} onSaved={load} />
      <AddToLibraryDialog open={addOpen} onOpenChange={setAddOpen} item={t} kind="technique" />
    </div>
  );
}
